import { useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  Compositor,
  DevTools,
  builtInRenderers,
  useEditor,
  UPDATE_PROPERTY,
  AudioReconciler,
} from '@/lib/kinema';
import type { ToolManager } from '@/lib/kinema';
import MotiWebSocket from '@/modules/createV3/MotiWebSocket';
import { MediaService } from '@services/media/MediaService';
import { PropertiesPanel } from '../panels/PropertiesPanel';
import { TimelinePanel } from '../timeline/TimelinePanel';
import { TranscodeProvider, type TranscodeContextValue } from '../transcodeContext';
import { AdTokenFindingsProvider } from '../adTokenFindings';
import { AdTokenDrawer } from './AdTokenDrawer';
import { STUDIO_AD_TOKENS, useFeatureFlag } from '@/shared/featureFlags/useFeatureFlag';
import { HeaderBar } from './HeaderBar';
import { FloatingToolbar } from './FloatingToolbar';
import { PanelResizeHandle } from './PanelResizeHandle';
import { TimelineResizeHandle } from './TimelineResizeHandle';

interface KinemaShellProps {
  onBack?: () => void;
  onMainLayerReady?: (layer: any) => void;
  /** Client feedback beside the canvas (MPN-1631). Omitted when there is none to show. */
  sidebar?: ReactNode;
}

const SIDEBAR_WIDTH = 300;

/** The shell's contexts: transcodes, and the ad token check across the composition (MPN-1933). */
function ShellProviders({ transcodeCtx, adTokensOn, children }: {
  transcodeCtx: TranscodeContextValue;
  adTokensOn: boolean;
  children: ReactNode;
}) {
  // Off, the provider reads nothing and finds nothing, so the header, layer list and Properties
  // panel show no ad tokens (MPN-2014). Disabled rather than unmounted: swapping the wrapper
  // when the flag arrives would remount the whole Studio under it.
  return (
    <TranscodeProvider value={transcodeCtx}>
      <AdTokenFindingsProvider enabled={adTokensOn}>{children}</AdTokenFindingsProvider>
    </TranscodeProvider>
  );
}

export function KinemaShell({ onBack, onMainLayerReady, sidebar }: KinemaShellProps) {
  const editor = useEditor();
  const adTokensOn = useFeatureFlag(STUDIO_AD_TOKENS);
  const [toolManager, setToolManager] = useState<ToolManager | null>(null);
  const [timelineHeight, setTimelineHeight] = useState(180);
  const [panelWidth, setPanelWidth] = useState(300);
  // The sidebar costs real width on a screen that is mostly canvas, so it collapses
  // (MPN-1631). Open by default: it exists to be read while editing, and a panel that
  // starts hidden is one nobody finds.
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [transcodingLayers, setTranscodingLayers] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    const reconciler = new AudioReconciler(editor);
    return () => reconciler.destroy();
  }, [editor]);

  const transcodeMovForLayer = useCallback((layerId: string, storageUri: string, name: string) => {
    setTranscodingLayers((prev) => new Map(prev).set(layerId, 0));

    (async () => {
      try {
        const motiSocket = new MotiWebSocket();
        await motiSocket.connect();
        const resultFile = await motiSocket.convert(storageUri, (percent) => {
          setTranscodingLayers((prev) => new Map(prev).set(layerId, percent));
        });

        if (resultFile) {
          const mediaService = new MediaService();
          const uploaded = await mediaService.uploadMedia(resultFile, name.replace(/\.mov$/i, '.webm'));
          const newSrc = uploaded.storage_uri || '';

          const vid = document.createElement('video');
          vid.preload = 'metadata';
          vid.onloadedmetadata = () => {
            let vw = vid.videoWidth;
            let vh = vid.videoHeight;
            const maxDim = 400;
            if (vw > maxDim || vh > maxDim) {
              const ratio = Math.min(maxDim / vw, maxDim / vh);
              vw = Math.round(vw * ratio);
              vh = Math.round(vh * ratio);
            }
            editor.batch(() => {
              editor.dispatchCommand(UPDATE_PROPERTY, { layerId, property: 'src', value: newSrc });
              editor.dispatchCommand(UPDATE_PROPERTY, { layerId, property: 'width', value: vw });
              editor.dispatchCommand(UPDATE_PROPERTY, { layerId, property: 'height', value: vh });
            });
            setTranscodingLayers((prev) => {
              const next = new Map(prev);
              next.delete(layerId);
              return next;
            });
          };
          vid.onerror = () => {
            editor.dispatchCommand(UPDATE_PROPERTY, { layerId, property: 'src', value: newSrc });
            setTranscodingLayers((prev) => {
              const next = new Map(prev);
              next.delete(layerId);
              return next;
            });
          };
          vid.src = newSrc;
        }
      } catch (err) {
        console.error('Transcode failed:', err);
        setTranscodingLayers((prev) => {
          const next = new Map(prev);
          next.delete(layerId);
          return next;
        });
      }
    })();
  }, [editor]);

  const transcodeCtx = { transcodingLayers, transcodeMovForLayer };

  return (
    <ShellProviders transcodeCtx={transcodeCtx} adTokensOn={adTokensOn}>
      <div
        style={{
          display: 'grid',
          gridTemplateRows: `auto 1fr ${timelineHeight}px auto`,
          // The canvas column is the 1fr, so collapsing the sidebar hands its width
          // straight back to the editor rather than leaving a gap.
          gridTemplateColumns: sidebar && sidebarOpen
            ? `${SIDEBAR_WIDTH}px 1fr ${panelWidth}px`
            : `1fr ${panelWidth}px`,
          height: '100vh',
          background: '#ffffff',
          color: '#1e293b',
        }}
      >
        <div style={{ gridColumn: '1 / -1' }}>
          <HeaderBar onBack={onBack} />
        </div>

        {sidebar && sidebarOpen && (
          <div style={{ overflowY: 'auto', overflowX: 'hidden', borderRight: '1px solid #e2e8f0' }}>
            {sidebar}
          </div>
        )}

        <div style={{ background: '#e2e8f0', overflow: 'hidden', position: 'relative' }}>
          {sidebar && (
            <button
              type="button"
              onClick={() => setSidebarOpen((open) => !open)}
              aria-expanded={sidebarOpen}
              aria-label={sidebarOpen ? 'Hide client feedback' : 'Show client feedback'}
              title={sidebarOpen ? 'Hide client feedback' : 'Show client feedback'}
              className="kinema-sidebar__toggle"
            >
              {sidebarOpen ? '\u2039' : '\u203a'}
            </button>
          )}
          <Compositor
            className="compositor-fill"
            renderers={builtInRenderers}
            onToolManagerReady={setToolManager}
            onMainLayerReady={onMainLayerReady}
          />
          <FloatingToolbar toolManager={toolManager} />
          {adTokensOn && <AdTokenDrawer />}
        </div>

        <div style={{ display: 'flex', overflow: 'hidden' }}>
          <PanelResizeHandle onResize={setPanelWidth} currentWidth={panelWidth} />
          <PropertiesPanel />
        </div>

        <div style={{ gridColumn: '1 / -1', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <TimelineResizeHandle onResize={setTimelineHeight} currentHeight={timelineHeight} />
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <TimelinePanel />
          </div>
        </div>

        <DevTools position="bottom" defaultOpen={false} />
      </div>
    </ShellProviders>
  );
}
